// `AppShell` (design-system spec §3.1) — ONE shell for every office and
// onboarding screen: module sidebar (216 px, desktop) and a 46 px topbar with
// tenant, global search and the session cluster.
//
// THE PHONE BAR (rev-7, D-426). Below 769 px the nav was a wrapping strip of
// every module above the content: 14 entries on four rows, about 175 px, so
// the first record of most lists started below the fold. It is now a bar fixed
// to the bottom of the glass with four destinations and More. The four are
// Home, the section the reader is in, then the next sections in nav order, so
// the reader always sees where they are and one tap takes them home. More
// opens a sheet with every other entry, each keeping its `nav-<key>` test id.
// A role with five entries or fewer sees them all and no More. Every entry is
// one tap from any screen, and the content reserves the bar's height so the
// bar never covers the last row.
//
// Presentational on purpose: the nav model (permission-gated links, live
// counts) is BUILT by routes/root.tsx and passed in, so this primitive imports
// no area code (§5 — a primitive may not import a screen) and renders
// chrome-complete on first paint (§5 render-once: counts are `undefined` until
// resolved and occupy reserved row space, never shifting geometry).

import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { StatusChip } from "./status-chip";
import { Toasts } from "./toast";
import { IconHelp, IconMore, IconSearch } from "./icons";
import { useDesktop } from "./use-desktop";

export interface ShellNavItem {
  key: string;
  to: string;
  label: string;
  icon?: ReactNode;
  testid?: string | undefined;
  /** Live count (11 px faint tabular). `undefined` renders nothing — a count
   * the backend cannot answer cheaply is absent, never faked. */
  count?: number | undefined;
  /** Red pill badge for counts that demand attention. */
  badge?: number | undefined;
  /** Extra chip content (the onboarding score keeps its `nav-chip`). */
  chip?: ReactNode;
  /** Render after the divider (settings-tier entries). */
  secondary?: boolean;
}

export interface AppShellProps {
  nav: readonly ShellNavItem[];
  /** §5 render-once: the nav is permission-decided chrome, so it stays a
   * dimension-reserving skeleton until the role feed has RESOLVED, then paints
   * once, complete. Feed failure still resolves (fail-closed absences), so the
   * skeleton is never eternal. */
  navReady: boolean;
  /** Tenant display name (real read; absent until it resolves). */
  tenant?: string | undefined;
  /** Environment chip label — shown only when the host says so (staging). */
  envChip?: string | undefined;
  /** Brand line under the product mark. */
  brandName: string;
  brandSub: string;
  searchLabel: string;
  searchPlaceholder: string;
  onSearch: (q: string) => void;
  /** Session cluster (locale switcher, sign out) — supplied by the layout. */
  topRight?: ReactNode;
  /** The customer guide (docs/spec/guide.md §7): a plain link, first in the
   * session cluster on every screen. The word on desktop; at phone width the
   * "?" mark alone with the word as its name, so it never crowds the bar. */
  help?: { href: string; label: string } | undefined;
  /** Rev-7 phone bar: the More button's word, the sheet's title and its close
   * button's word. Without them the phone bar shows no More and holds every
   * entry, which only a role with five entries or fewer should rely on. */
  phoneMore?: { label: string; title: string; closeLabel: string } | undefined;
  children: ReactNode;
}

/** How many entries the phone bar shows before More (rev-7). */
export const PHONE_BAR_SLOTS = 4;

/** The number of path segments `to` shares with `pathname`, from the start. */
function sharedSegments(to: string, pathname: string): number {
  const a = to.split("/").filter(Boolean);
  const b = pathname.split("/").filter(Boolean);
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n += 1;
  return n;
}

/** The entry the reader is in: the one whose path shares the most leading
 * segments with the address, or none when no entry shares one. */
export function activeNavKey(nav: readonly ShellNavItem[], pathname: string): string | undefined {
  let best: { key: string; score: number } | undefined;
  for (const item of nav) {
    const score = sharedSegments(item.to, pathname);
    if (score > 0 && (best === undefined || score > best.score)) best = { key: item.key, score };
  }
  return best?.key;
}

/**
 * Splits the nav into the phone bar's entries and More's (rev-7). A nav of
 * five or fewer goes in the bar whole. Otherwise the bar holds Home, the
 * active entry, then primary entries in nav order, up to four, and More holds
 * the rest in nav order.
 */
export function splitPhoneNav(
  nav: readonly ShellNavItem[],
  activeKey: string | undefined,
): { bar: ShellNavItem[]; more: ShellNavItem[] } {
  if (nav.length <= PHONE_BAR_SLOTS + 1) return { bar: [...nav], more: [] };
  const picked: ShellNavItem[] = [];
  const take = (item: ShellNavItem | undefined): void => {
    if (item !== undefined && !picked.includes(item) && picked.length < PHONE_BAR_SLOTS) {
      picked.push(item);
    }
  };
  take(nav.find((n) => n.key === "home"));
  take(nav.find((n) => n.key === activeKey));
  for (const item of nav) if (item.secondary !== true) take(item);
  for (const item of nav) take(item);
  const bar = nav.filter((n) => picked.includes(n));
  const more = nav.filter((n) => !picked.includes(n));
  return { bar, more };
}

function NavLinks({
  nav,
  onNavigate,
}: {
  nav: readonly ShellNavItem[];
  onNavigate?: (() => void) | undefined;
}) {
  const primary = nav.filter((n) => n.secondary !== true);
  const secondary = nav.filter((n) => n.secondary === true);
  const renderItem = (item: ShellNavItem) => (
    <Link
      key={item.key}
      to={item.to}
      data-testid={item.testid}
      activeProps={{ className: "active" }}
      onClick={onNavigate}
    >
      {item.icon}
      <span className="ds-nav-label">{item.label}</span>
      {item.chip}
      {item.badge !== undefined && item.badge > 0 ? (
        <span className="ds-nav-bd">{item.badge}</span>
      ) : item.count !== undefined ? (
        <span className="ds-nav-ct">{item.count}</span>
      ) : null}
    </Link>
  );
  return (
    <>
      {primary.map(renderItem)}
      {secondary.length > 0 ? <div className="ds-nav-dv" role="presentation" /> : null}
      {secondary.map(renderItem)}
    </>
  );
}

/** The rev-7 phone bar: four destinations and More, fixed to the bottom. */
function PhoneBar({
  nav,
  more,
}: {
  nav: readonly ShellNavItem[];
  more: AppShellProps["phoneMore"];
}) {
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const [open, setOpen] = useState(false);
  const sheetId = useId();
  const moreButton = useRef<HTMLButtonElement | null>(null);
  const sheet = useRef<HTMLDivElement | null>(null);
  const split =
    more === undefined
      ? { bar: [...nav], more: [] }
      : splitPhoneNav(nav, activeNavKey(nav, pathname));

  // A route change closes the sheet: a link inside it, browser Back, or a
  // navigation the page made on its own all leave the sheet's list stale.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    sheet.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        moreButton.current?.focus();
        return;
      }
      // The sheet is modal, so Tab cycles inside it and never reaches the
      // page behind the scrim.
      if (e.key === "Tab" && sheet.current !== null) {
        const focusables = Array.from(
          sheet.current.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (first === undefined || last === undefined) return;
        const active = document.activeElement;
        const inside = active instanceof Node && sheet.current.contains(active);
        if (e.shiftKey && (!inside || active === first || active === sheet.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (!inside || active === last)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <nav className="app-nav ds-nav ds-nav-bar" data-testid="app-nav">
        {split.bar.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            data-testid={item.testid}
            activeProps={{ className: "active" }}
          >
            {item.icon}
            <span className="ds-nav-label">{item.label}</span>
            {item.badge !== undefined && item.badge > 0 ? (
              <span className="ds-nav-bd">{item.badge}</span>
            ) : (
              item.chip
            )}
          </Link>
        ))}
        {split.more.length > 0 && more !== undefined ? (
          <button
            type="button"
            ref={moreButton}
            className="ds-nav-more"
            aria-expanded={open}
            aria-controls={open ? sheetId : undefined}
            data-testid="nav-more"
            onClick={() => {
              setOpen((v) => !v);
            }}
          >
            <IconMore />
            <span className="ds-nav-label">{more.label}</span>
            {/* An entry behind More that asks for attention marks More, so a
                red count is never one tap out of sight with no sign. */}
            {split.more.some((item) => item.badge !== undefined && item.badge > 0) ? (
              <span className="ds-nav-dot" data-testid="nav-more-dot" aria-hidden="true" />
            ) : null}
          </button>
        ) : null}
      </nav>
      {open && more !== undefined ? (
        <>
          <div
            className="ds-nav-scrim"
            aria-hidden="true"
            onClick={() => {
              setOpen(false);
            }}
          />
          <div
            ref={sheet}
            id={sheetId}
            role="dialog"
            aria-modal="true"
            aria-label={more.title}
            tabIndex={-1}
            className="ds-nav-sheet"
            data-testid="nav-more-sheet"
          >
            <div className="ds-nav-sheet-head">
              <p className="ds-nav-sheet-title">{more.title}</p>
              <button
                type="button"
                className="ds-btn"
                onClick={() => {
                  setOpen(false);
                  moreButton.current?.focus();
                }}
              >
                {more.closeLabel}
              </button>
            </div>
            <nav className="ds-nav ds-nav-sheet-list" aria-label={more.title}>
              <NavLinks
                nav={split.more}
                onNavigate={() => {
                  setOpen(false);
                }}
              />
            </nav>
          </div>
        </>
      ) : null}
    </>
  );
}

function NavSkeleton() {
  return (
    <div className="ds-nav-skel" data-testid="app-nav-skeleton" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <span key={i} className="skeleton" />
      ))}
    </div>
  );
}

export function AppShell({
  nav,
  navReady,
  tenant,
  envChip,
  brandName,
  brandSub,
  searchLabel,
  searchPlaceholder,
  onSearch,
  topRight,
  help,
  phoneMore,
  children,
}: AppShellProps) {
  const desktop = useDesktop();
  const [q, setQ] = useState("");

  const submit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const trimmed = q.trim();
    if (trimmed.length > 0) onSearch(trimmed);
  };

  const brand = (
    <div className="ds-brand">
      <span className="ds-brand-mk" aria-hidden="true">
        A
      </span>
      <div>
        <span className="ds-brand-name">{brandName}</span>
        <span className="ds-brand-sub">{brandSub}</span>
      </div>
    </div>
  );

  return (
    <div className={`app ds-shell${desktop ? "" : " ds-shell-phone"}`}>
      {desktop ? (
        <aside className="ds-side">
          {brand}
          {navReady ? (
            <nav className="app-nav ds-nav" data-testid="app-nav">
              <NavLinks nav={nav} />
            </nav>
          ) : (
            <NavSkeleton />
          )}
        </aside>
      ) : null}
      <div className="ds-main">
        <header className="ds-topbar" data-testid="app-topbar">
          {tenant !== undefined ? (
            <span className="ds-tenant" data-testid="shell-tenant">
              {tenant}
            </span>
          ) : (
            <span className="ds-tenant" data-testid="shell-tenant" aria-hidden="true">
              {" "}
            </span>
          )}
          {desktop ? (
            <form className="ds-gsearch" role="search" onSubmit={submit}>
              <IconSearch />
              <input
                type="search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                }}
                aria-label={searchLabel}
                placeholder={searchPlaceholder}
                data-testid="global-search"
              />
            </form>
          ) : null}
          <div className="ds-top-right">
            {envChip !== undefined ? (
              <StatusChip variant="neutral" data-testid="env-chip">
                {envChip}
              </StatusChip>
            ) : null}
            {help !== undefined ? (
              desktop ? (
                <a className="ds-lnk ds-top-help" href={help.href} data-testid="help-link">
                  {help.label}
                </a>
              ) : (
                <a
                  className="ds-top-help ds-top-help-mark"
                  href={help.href}
                  aria-label={help.label}
                  title={help.label}
                  data-testid="help-link"
                >
                  <IconHelp />
                </a>
              )
            ) : null}
            {topRight}
          </div>
        </header>
        {!desktop ? (
          navReady ? (
            <PhoneBar nav={nav} more={phoneMore} />
          ) : (
            <div className="ds-nav-bar ds-nav-bar-skel" aria-hidden="true">
              <NavSkeleton />
            </div>
          )
        ) : null}
        <main className="ds-content">{children}</main>
      </div>
      <Toasts />
    </div>
  );
}
